import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, Drawer, Grid, IconButton, Table, TableCell, TableHead, TableRow, Typography, Skeleton, Popover, Tooltip, Switch, FormControlLabel } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { setRevisonHistory } from "../actions";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import { globalGetService } from "../../utils/globalApiServices";
import { gaTelesisInstance } from "..";

const DEFAULT_PAGE_SIZE = 50;
const toCamelCase = (str) => { if (!str) return ""; const cleanedStr = str.replace(/_?id$/i, ""); return cleanedStr.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" "); };
const RESERVE_PBH = [
  { key: "range_label", label: "Range" },
  { key: "min", label: "Min" },
  { key: "max", label: "Max" },
  { key: "rate", label: "Rate" },
  { key: "esc_percentage", label: "Esc %" },
  { key: "esc_date", label: "Esc Date" },
  { key: "rental_rate_type", label: "Rate Type" },
];
const RENTAL_PBH = [
  { key: "unit_of_measure_for", label: "Unit", optional: true }, // FH / FC split, hidden when the rows don't carry it
  { key: "range_condition", label: "Range Condition" },
  { key: "min_value", label: "Min" },
  { key: "max_value", label: "Max" },
  { key: "rate", label: "Rate" },
  { key: "esc_percentage", label: "Esc %" },
  { key: "esc_date", label: "Esc Date" },
  { key: "rate_type", label: "Rate Type" },
];
// interval_condition rows can have any shape (rate/ratio, rate/derate, nested "ratios" arrays, etc.)
// and old/new can each use different keys, so columns are derived from the actual data instead of a fixed list.
const buildDynamicColumns = (rows) => {
  if (!Array.isArray(rows)) return [];
  const keys = [];
  rows.forEach((row) => {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      Object.keys(row).forEach((key) => {
        if (key === "type") return; // not shown in revision history
        if (!keys.includes(key)) keys.push(key);
      });
    }
  });
  return keys.map((key) => ({ key, label: toCamelCase(key) }));
};
const ENGINE_THRUST_TYPES_COLUMNS = [
  { key: "name", label: "Name" },
];
const CUSTOM_PAYMENT_COLUMNS = [
  { key: "period", label: "Calculation Period" },
  { key: "payment_date", label: "Payment Date" },
  { key: "rent", label: "Rent" },
  { key: "basic_rent", label: "Basic Rent" },
  { key: "reference_value", label: "Reference Value" },
];
const INSURANCE_VARIABILITY_COLUMNS = [
  { key: "start_date", label: "Start Date" },
  { key: "end_date", label: "End Date" },
  { key: "amount", label: "Amount" },
  { key: "variability", label: "Variablity" },
];


const PBH_CODE_LABEL_MAPS = {
  range_condition: { 1: "Less Than Equal To", 2: "In Between Equal To", 3: "Greater Than Equal To" },
  rate_type: { 1: "Fixed", 2: "Per Unit" },
};

const formatPbhConditionCell = (row, key) => { // handle for BOTH Reserve and Rental, plus dynamic interval condition
  const value = row?.[key];
  if (value === null || value === undefined || value === "") return "-";
  if (key === "esc_date" || key === "payment_date" || key === "start_date" || key === "end_date") return moment(value).format("DD MMM YYYY");
  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    const isObjectRows = value.every((item) => item && typeof item === "object" && !Array.isArray(item));
    if (!isObjectRows) return value.join(", ");
    const nestedColumns = buildDynamicColumns(value);
    return (
      <Table size="small" className="mui-table-format">
        <TableHead>
          <TableRow>
            {nestedColumns.map((col) => (
              <TableCell key={col.key}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <tbody>
          {value.map((nestedRow, idx) => (
            <TableRow key={idx}>
              {nestedColumns.map((col) => (
                <TableCell key={col.key}>{formatPbhConditionCell(nestedRow, col.key)}</TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </Table>
    );
  }
  if (typeof value === "object") return value?.label || "-";
  if (PBH_CODE_LABEL_MAPS[key]) return PBH_CODE_LABEL_MAPS[key][value] ?? value;
  return value;
};

const isNestedRows = (value) =>
  Array.isArray(value) && value.length > 0 && value.every((item) => item && typeof item === "object" && !Array.isArray(item));

// Derate based ratio rows carry a nested table per row (e.g. derate -> ratios[]). Rendering a table inside
// every cell makes the popover taller than the screen, so show one row per parent (derate) and lay the
// nested rows out horizontally as compact tiles that wrap across a wide (landscape) popover.
const buildFlatLayout = (rows, columns) => {
  const nestedKeys = columns.filter((col) => rows.some((row) => isNestedRows(row?.[col.key]))).map((col) => col.key);
  if (!nestedKeys.length) return null;
  return {
    parentColumns: columns.filter((col) => !nestedKeys.includes(col.key)),
    nestedGroups: nestedKeys.map((key) => ({
      key,
      label: toCamelCase(key),
      columns: buildDynamicColumns(rows.flatMap((row) => (isNestedRows(row?.[key]) ? row[key] : []))),
    })),
  };
};

const hasCellValue = (value) => value !== null && value !== undefined && value !== "" && !(Array.isArray(value) && !value.length);
const visibleColumns = (rows, columns = []) =>
  columns.filter((col) => !col.optional || rows.some((row) => hasCellValue(row?.[col.key])));

const STICKY_CELL_SX = { position: "sticky", left: 0, borderRight: "1px solid #dde2f0" };

const NestedRowTiles = ({ rows, columns }) => {
  if (!rows.length) return "-";
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 0.75 }}>
      {rows.map((nestedRow, idx) => (
        <Box
          key={idx}
          sx={{ display: "flex", border: "1px solid #dde2f0", borderRadius: 1, overflow: "hidden", backgroundColor: "#fff", fontSize: 13 }}
        >
          {columns.map((col, colIdx) => (
            <Box
              key={col.key}
              sx={{
                flex: 1,
                px: 1,
                py: 0.5,
                textAlign: "center",
                whiteSpace: "nowrap",
                ...(colIdx === 0 ? { fontWeight: 600, backgroundColor: "#eef1f9" } : { borderLeft: "1px solid #dde2f0" }),
              }}
            >
              {formatPbhConditionCell(nestedRow, col.key)}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const FlatConditionRows = ({ rows, layout, headerHeight }) => {
  const { parentColumns, nestedGroups } = layout;
  return rows.map((row, idx) => {
    const backgroundColor = idx % 2 ? "#f5f7fc" : "#fff";
    return (
      <TableRow key={idx} sx={{ backgroundColor, "& > td": { verticalAlign: "top", borderBottom: "1px solid #dde2f0" } }}>
        {parentColumns.map((col, colIdx) => (
          <TableCell
            key={col.key}
            sx={{ fontWeight: 600, whiteSpace: "nowrap", ...(colIdx === 0 ? { ...STICKY_CELL_SX, zIndex: 1, backgroundColor } : {}) }}
          >
            {colIdx === 0 ? (
              <Box sx={{ position: "sticky", top: headerHeight + 6 }}>{formatPbhConditionCell(row, col.key)}</Box>
            ) : (
              formatPbhConditionCell(row, col.key)
            )}
          </TableCell>
        ))}
        {nestedGroups.map((group) => (
          <TableCell key={group.key} sx={{ width: "100%" }}>
            <NestedRowTiles rows={isNestedRows(row?.[group.key]) ? row[group.key] : []} columns={group.columns} />
          </TableCell>
        ))}
      </TableRow>
    );
  });
};

// ---------- Old vs New comparison ----------
// Old and new condition lists are shown in separate popovers, which makes it hard to spot what actually changed.
// Row identity used to pair old and new rows, first candidate whose value is present and unique on both sides wins.
// Range tables (rental PBH / range based interval) repeat range_condition ("In Between" many times, and per FH/FC
// unit), so a range is identified by where it starts: unit + min value.
const DIFF_KEY_CANDIDATES = [
  ["derate"], ["ratio"], ["name"], ["range_label"], ["unit_of_measure_for", "min_value"], ["min_value"], ["min"], ["period"], ["start_date"],];
const DIFF_STATUS_ORDER = ["added", "removed", "modified", "unchanged"];
const DIFF_STYLES = {
  added: { label: "Added", color: "#2e7d32", bg: "#edf7ee" },
  removed: { label: "Removed", color: "#c62828", bg: "#fdeded" },
  modified: { label: "Modified", color: "#e65100", bg: "#fff4e5" },
  unchanged: { label: "Unchanged", color: "#6b7280", bg: "#fff" },
};
const TILE_PREVIEW_LIMIT = 14;

const normalizeScalar = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) return value.map(normalizeScalar).join(",");
  if (typeof value === "object") return String(value?.label ?? value?.value ?? value?.name ?? JSON.stringify(value));
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}([T ].*)?$/.test(str)) return str.slice(0, 10);
  // compare numbers by value so 195 / "195.00" / "195" match, but 195 vs 195.01 is still a change
  const num = Number(str.replace(/,/g, ""));
  return str !== "" && !isNaN(num) ? String(num) : str;
};

const rowId = (row, keys) => keys.map((key) => normalizeScalar(row[key])).join("\u0000");

const pickRowKey = (oldRows, newRows) => {
  const allRows = [...oldRows, ...newRows];
  const isUnique = (rows, keys) => new Set(rows.map((row) => rowId(row, keys))).size === rows.length;
  return (
    DIFF_KEY_CANDIDATES.find(
      (keys) =>
        allRows.every((row) => row && keys.every((key) => row[key] !== undefined && row[key] !== null && row[key] !== "")) &&
        isUnique(oldRows, keys) &&
        isUnique(newRows, keys)
    ) || null
  );
};

const compareIdPart = (a, b) => {
  const numA = Number(a);
  const numB = Number(b);
  return a !== "" && b !== "" && !isNaN(numA) && !isNaN(numB) ? numA - numB : String(a).localeCompare(String(b));
};

const compareRow = (oldRow, newRow) => {
  const keys = [...new Set([...Object.keys(oldRow || {}), ...Object.keys(newRow || {})])].filter((key) => key !== "type");
  const changedKeys = [];
  const nested = {};
  keys.forEach((key) => {
    const oldVal = oldRow?.[key];
    const newVal = newRow?.[key];
    if (isNestedRows(oldVal) || isNestedRows(newVal)) {
      const nestedDiff = diffRows(isNestedRows(oldVal) ? oldVal : [], isNestedRows(newVal) ? newVal : []);
      nested[key] = nestedDiff;
      if (nestedDiff.some((entry) => entry.status !== "unchanged")) changedKeys.push(key);
    } else if (normalizeScalar(oldVal) !== normalizeScalar(newVal)) {
      changedKeys.push(key);
    }
  });
  return { status: changedKeys.length ? "modified" : "unchanged", row: newRow, oldRow, changedKeys, nested };
};

const diffRows = (oldRows = [], newRows = []) => {
  const keys = pickRowKey(oldRows, newRows);
  const idOf = (row, idx) => (keys ? rowId(row, keys) : String(idx));
  const oldById = new Map(oldRows.map((row, idx) => [idOf(row, idx), row]));
  const newIds = new Set(newRows.map(idOf));
  const entries = newRows.map((row, idx) => {
    const oldRow = oldById.get(idOf(row, idx));
    return oldRow ? compareRow(oldRow, row) : { status: "added", row, changedKeys: [] };
  });
  oldRows.forEach((row, idx) => {
    if (!newIds.has(idOf(row, idx))) entries.push({ status: "removed", row, changedKeys: [] });
  });
  if (keys) {
    entries.sort((a, b) => {
      for (const key of keys) {
        const diff = compareIdPart(normalizeScalar(a.row[key]), normalizeScalar(b.row[key]));
        if (diff) return diff;
      }
      return 0;
    });
  }
  return entries;
};

const countStatuses = (entries) =>
  entries.reduce((acc, entry) => ({ ...acc, [entry.status]: acc[entry.status] + 1 }), { added: 0, removed: 0, modified: 0, unchanged: 0 });

const DiffChip = ({ status, count, suffix = true }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 0.75,
      borderRadius: 1,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: "18px",
      color: DIFF_STYLES[status].color,
      backgroundColor: status === "unchanged" ? "#f1f2f5" : DIFF_STYLES[status].bg,
      border: `1px solid ${DIFF_STYLES[status].color}33`,
      whiteSpace: "nowrap",
    }}
  >
    {count !== undefined ? `${count} ` : ""}
    {suffix ? DIFF_STYLES[status].label.toLowerCase() : DIFF_STYLES[status].label}
  </Box>
);

const ValueChange = ({ entry, colKey }) => (
  <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.25 }}>
    <Box component="span" sx={{ textDecoration: "line-through", color: DIFF_STYLES.removed.color, fontSize: 11 }}>
      {formatPbhConditionCell(entry.oldRow, colKey)}
    </Box>
    <Box component="span" sx={{ color: DIFF_STYLES.added.color, fontWeight: 700 }}>
      {formatPbhConditionCell(entry.row, colKey)}
    </Box>
  </Box>
);

const renderDiffValue = (entry, colKey) =>
  entry.status === "modified" && entry.changedKeys.includes(colKey) ? (
    <ValueChange entry={entry} colKey={colKey} />
  ) : (
    formatPbhConditionCell(entry.row, colKey)
  );

const DiffTiles = ({ entries, columns, showUnchanged }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = showUnchanged ? entries : entries.filter((entry) => entry.status !== "unchanged");
  const hiddenCount = entries.length - visible.length;
  const shown = expanded ? visible : visible.slice(0, TILE_PREVIEW_LIMIT);
  if (!entries.length) return "-";
  return (
    <Box>
      {shown.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 0.75 }}>
          {shown.map((entry, idx) => {
            const style = DIFF_STYLES[entry.status];
            return (
              <Tooltip key={idx} title={style.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    border: `1px solid ${entry.status === "unchanged" ? "#dde2f0" : style.color}`,
                    borderRadius: 1,
                    overflow: "hidden",
                    backgroundColor: style.bg,
                    fontSize: 13,
                    ...(entry.status === "removed" ? { opacity: 0.85 } : {}),
                  }}
                >
                  {columns.map((col, colIdx) => (
                    <Box
                      key={col.key}
                      sx={{
                        flex: 1,
                        px: 1,
                        py: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap",
                        ...(entry.status === "removed" ? { textDecoration: "line-through", color: style.color } : {}),
                        ...(entry.status === "added" ? { color: style.color, fontWeight: 600 } : {}),
                        ...(colIdx > 0 ? { borderLeft: "1px solid #dde2f0" } : { fontWeight: 600 }),
                      }}
                    >
                      {renderDiffValue(entry, col.key)}
                    </Box>
                  ))}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1.5, mt: shown.length ? 0.75 : 0, alignItems: "center" }}>
        {visible.length > TILE_PREVIEW_LIMIT && (
          <Typography variant="caption" color="primary" sx={{ cursor: "pointer", fontWeight: 600 }} onClick={() => setExpanded(!expanded)}>
            {expanded ? "Show less" : `Show all ${visible.length}`}
          </Typography>
        )}
        {hiddenCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {hiddenCount} unchanged hidden
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Fixed-column tables (PBH, custom payments, variabilities, thrust types) are compared only on the columns they
// display, so ids / timestamps / optional keys the backend adds can't show up as a change or as a format change.
const toComparableRows = (value, compareColumns) => {
  const rows = Array.isArray(value) ? value.filter((row) => row && typeof row === "object") : [];
  if (!compareColumns) return rows;
  return rows.map((row) => Object.fromEntries(compareColumns.map((col) => [col.key, row[col.key]])));
};

// Shape of a condition table, e.g. "derate|ratios(rate|ratio)" or "derate|rate". When the reserve basis
// changes (Ratio & DeRate -> DeRate, etc.) old and new rows have different shapes and can't be matched row by row.
const tableSignature = (rows) => {
  const keys = new Set();
  rows.forEach((row) =>
    Object.keys(row || {}).forEach((key) => {
      if (key === "type") return;
      keys.add(isNestedRows(row[key]) ? `${key}(${buildDynamicColumns(row[key]).map((col) => col.key).sort().join("|")})` : key);
    })
  );
  return [...keys].sort().join("|");
};

const describeColumns = (rows, columns) => {
  const layout = buildFlatLayout(rows, columns);
  if (!layout) return columns.map((col) => col.label).join(" | ");
  return [
    ...layout.parentColumns.map((col) => col.label),
    ...layout.nestedGroups.map((group) => `${group.label} (${group.columns.map((col) => col.label).join(" | ")})`),
  ].join(" | ");
};

const DiffTable = ({ entries, columns, showUnchanged, emptyText }) => {
  const flatLayout = useMemo(() => buildFlatLayout(entries.flatMap((entry) => [entry.row, entry.oldRow].filter(Boolean)), columns), [entries, columns]);
  const visibleRows = showUnchanged ? entries : entries.filter((entry) => entry.status !== "unchanged");
  const tableColumns = visibleColumns(
    entries.flatMap((entry) => [entry.row, entry.oldRow].filter(Boolean)),
    flatLayout ? flatLayout.parentColumns : columns
  );
  return (
    <Table size="small" className="mui-table-format" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ whiteSpace: "nowrap", zIndex: 2 }}>Change</TableCell>
          {tableColumns.map((col) => (
            <TableCell key={col.key} sx={{ whiteSpace: "nowrap", zIndex: 2 }}>{col.label}</TableCell>
          ))}
          {flatLayout?.nestedGroups.map((group) => (
            <TableCell key={group.key} sx={{ whiteSpace: "nowrap", zIndex: 2 }}>
              {`${group.label} (${group.columns.map((col) => col.label).join(" | ")})`}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <tbody>
        {visibleRows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={tableColumns.length + (flatLayout?.nestedGroups.length || 0) + 1} align="center">
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          visibleRows.map((entry, idx) => {
            const style = DIFF_STYLES[entry.status];
            return (
              <TableRow
                key={idx}
                sx={{
                  backgroundColor: entry.status === "unchanged" ? "#fff" : style.bg,
                  "& > td": { verticalAlign: "top", borderBottom: "1px solid #dde2f0" },
                  "& > td:first-of-type": { borderLeft: `4px solid ${style.color}` },
                }}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <DiffChip status={entry.status} suffix={false} />
                  {entry.status === "modified" &&
                    flatLayout?.nestedGroups.map((group) => {
                      const nestedCounts = countStatuses(entry.nested?.[group.key] || []);
                      const changed = nestedCounts.added + nestedCounts.removed + nestedCounts.modified;
                      return changed ? (
                        <Typography key={group.key} variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {changed} of {(entry.nested?.[group.key] || []).length} {group.label.toLowerCase()} changed
                        </Typography>
                      ) : null;
                    })}
                </TableCell>
                {tableColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      ...(entry.status === "removed" ? { textDecoration: "line-through", color: style.color } : {}),
                      ...(entry.status === "added" ? { color: style.color } : {}),
                    }}
                  >
                    {renderDiffValue(entry, col.key)}
                  </TableCell>
                ))}
                {flatLayout?.nestedGroups.map((group) => {
                  const nestedEntries =
                    entry.nested?.[group.key] ??
                    (isNestedRows(entry.row?.[group.key]) ? entry.row[group.key].map((row) => ({ status: entry.status, row, changedKeys: [] })) : []);
                  return (
                    <TableCell key={group.key} sx={{ width: "100%" }}>
                      <DiffTiles entries={nestedEntries} columns={group.columns} showUnchanged={showUnchanged} />
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })
        )}
      </tbody>
    </Table>
  );
};

const ConditionChangesCell = ({ oldValue, newValue, oldColumns, newColumns, compareColumns }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const comparison = useMemo(() => {
    const oldRows = toComparableRows(oldValue, compareColumns);
    const newRows = toComparableRows(newValue, compareColumns);
    const structureChanged = oldRows.length > 0 && newRows.length > 0 && tableSignature(oldRows) !== tableSignature(newRows);
    if (structureChanged) {
      const removed = oldRows.map((row) => ({ status: "removed", row, changedKeys: [] }));
      const added = newRows.map((row) => ({ status: "added", row, changedKeys: [] }));
      return {
        structureChanged,
        removed,
        added,
        oldFormat: describeColumns(oldRows, oldColumns),
        newFormat: describeColumns(newRows, newColumns),
        counts: countStatuses([...removed, ...added]),
      };
    }
    const diff = diffRows(oldRows, newRows);
    const columnMap = new Map([...oldColumns, ...newColumns].map((col) => [col.key, col]));
    return { structureChanged, diff, columns: [...columnMap.values()], counts: countStatuses(diff) };
  }, [oldValue, newValue, oldColumns, newColumns, compareColumns]);
  const { counts, structureChanged } = comparison;
  const hasChanges = counts.added + counts.removed + counts.modified > 0;

  return (
    <>
      <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        {structureChanged ? (
          <DiffChip status="modified" count={undefined} suffix={false} />
        ) : hasChanges ? (
          DIFF_STATUS_ORDER.filter((status) => status !== "unchanged" && counts[status]).map((status) => (
            <DiffChip key={status} status={status} count={counts[status]} />
          ))
        ) : (
          <Typography variant="caption" color="text.secondary">No value changes</Typography>
        )}
        {structureChanged && <Typography variant="caption" color="text.secondary">Table format changed</Typography>}
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: "pointer", fontWeight: 600, textDecoration: "underline", ml: 0.5 }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          Compare
        </Typography>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorReference="anchorPosition"
        anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { display: "flex", flexDirection: "column", maxHeight: "85vh", width: "min(1040px, 94vw)", overflow: "hidden" } } }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #dde2f0" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Changes (Old → New)</Typography>
            <IconButton size="small" onClick={() => setAnchorEl(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {structureChanged ? (
            <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, backgroundColor: DIFF_STYLES.modified.bg, border: `1px solid ${DIFF_STYLES.modified.color}55` }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: DIFF_STYLES.modified.color }}>Table format changed</Typography>
              <Typography variant="caption" component="div">
                <Box component="span" sx={{ textDecoration: "line-through", color: DIFF_STYLES.removed.color }}>{comparison.oldFormat}</Box>
                {"  →  "}
                <Box component="span" sx={{ color: DIFF_STYLES.added.color, fontWeight: 700 }}>{comparison.newFormat}</Box>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All {counts.removed} old {counts.removed === 1 ? "row was" : "rows were"} replaced by {counts.added} new {counts.added === 1 ? "row" : "rows"}.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                {DIFF_STATUS_ORDER.map((status) => (
                  <DiffChip key={status} status={status} count={counts[status]} />
                ))}
                <FormControlLabel
                  sx={{ ml: "auto", mr: 0 }}
                  control={<Switch size="small" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} />}
                  label={<Typography variant="caption">Show unchanged</Typography>}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Changed values show the old value{" "}
                <Box component="span" sx={{ textDecoration: "line-through", color: DIFF_STYLES.removed.color }}>struck through</Box> above the{" "}
                <Box component="span" sx={{ color: DIFF_STYLES.added.color, fontWeight: 700 }}>new value</Box>.
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", px: 1, pb: 1 }}>
          {structureChanged ? (
            <>
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: DIFF_STYLES.removed.color, mt: 1, mb: 0.5 }}>
                Old table (removed)
              </Typography>
              <DiffTable entries={comparison.removed} columns={oldColumns} showUnchanged emptyText="No records" />
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: DIFF_STYLES.added.color, mt: 2, mb: 0.5 }}>
                New table (added)
              </Typography>
              <DiffTable entries={comparison.added} columns={newColumns} showUnchanged emptyText="No records" />
            </>
          ) : (
            <DiffTable
              entries={comparison.diff}
              columns={comparison.columns}
              showUnchanged={showUnchanged}
              emptyText={hasChanges ? "No records" : "Only number formatting differs, the values are the same (e.g. 195 and 195.00)"}
            />
          )}
        </Box>
      </Popover>
    </>
  );
};

const onlyChangedRows = (ownRows, otherRows, side) => {
  if (!ownRows.length || !otherRows.length) return null;
  const oldRows = side === "old" ? ownRows : otherRows;
  const newRows = side === "old" ? otherRows : ownRows;
  if (tableSignature(oldRows) !== tableSignature(newRows)) return null;
  const ownStatus = side === "old" ? "removed" : "added";
  const pick = (entry) => (entry.status === ownStatus ? entry.row : side === "old" ? entry.oldRow : entry.row);
  return diffRows(oldRows, newRows)
    .filter((entry) => entry.status === ownStatus || entry.status === "modified")
    .map((entry) => {
      const row = { ...pick(entry) };
      // a modified row is worth showing on this side only if a plain value changed or some nested rows are left
      let hasOwnChange = entry.status === ownStatus || entry.changedKeys.some((key) => !entry.nested?.[key]);
      Object.entries(entry.nested || {}).forEach(([key, nestedEntries]) => {
        row[key] = nestedEntries.filter((nested) => nested.status === ownStatus || nested.status === "modified").map(pick);
        if (row[key].length) hasOwnChange = true;
      });
      return hasOwnChange ? row : null;
    })
    .filter(Boolean);
};

const PbhConditionCell = ({ value, otherValue, type, columns = RESERVE_PBH, compareColumns }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const allRows = Array.isArray(value) ? value : [];
  const changedRows = useMemo(
    () =>
      otherValue === undefined
        ? null
        : onlyChangedRows(toComparableRows(value, compareColumns), toComparableRows(otherValue, compareColumns), type),
    [value, otherValue, type, compareColumns]
  );
  const rows = changedRows && !showAll ? changedRows : allRows;
  const open = Boolean(anchorEl);
  const [headerHeight, setHeaderHeight] = useState(0);
  const measureHeader = (node) => {
    if (node && node.offsetHeight !== headerHeight) setHeaderHeight(node.offsetHeight);
  };
  const flatLayout = open ? buildFlatLayout(rows, columns || []) : null;
  const headerColumns = flatLayout
    ? [
      ...flatLayout.parentColumns,
      ...flatLayout.nestedGroups.map((group) => ({
        key: group.key,
        label: `${group.label} (${group.columns.map((col) => col.label).join(" | ")})`,
      })),
    ]
    : visibleColumns(rows, columns || []);
  const popoverPosition = flatLayout ? { anchorReference: "anchorPosition", anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 }, transformOrigin: { vertical: "center", horizontal: "center" }, } : { anchorEl, anchorOrigin: { vertical: "bottom", horizontal: "left" } };

  return (
    <>
      <Tooltip title={type === "old" ? "Click to show Old value" : "Click to show New value"}>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <InfoOutlinedIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        {...popoverPosition}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              maxHeight: flatLayout ? "80vh" : "70vh",
              ...(flatLayout ? { width: "min(960px, 92vw)" } : { maxWidth: "min(640px, 90vw)", minWidth: 260 }),
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: 1.5, py: 1, borderBottom: "1px solid #dde2f0" }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {type === "old" ? "Old Value" : "New Value"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {changedRows && !showAll
                ? `Showing only changed records: ${changedRows.length} of ${allRows.length}`
                : `${allRows.length} ${allRows.length === 1 ? "record" : "records"}`}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {changedRows && (
              <FormControlLabel
                sx={{ mr: 0 }}
                control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
                label={<Typography variant="caption">Show all</Typography>}
              />
            )}
            <IconButton size="small" onClick={() => setAnchorEl(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {/* no top padding: a sticky header sticks below it and body rows would show through the gap */}
        <Box sx={{ flex: 1, overflow: "auto", px: 1, pb: 1 }}>
          <Table size="small" className="mui-table-format" stickyHeader>
            <TableHead ref={measureHeader}>
              <TableRow>
                {headerColumns.map((col, colIdx) => (
                  <TableCell
                    key={col.key}
                    sx={{ whiteSpace: "nowrap", zIndex: 2, ...(flatLayout && colIdx === 0 ? { ...STICKY_CELL_SX, zIndex: 3 } : {}) }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <tbody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headerColumns.length || 1} align="center">
                    {changedRows && !showAll
                      ? type === "old"
                        ? "No old values were removed or changed (only new records were added)"
                        : "No new values were added or changed (only old records were removed)"
                      : "No records found"}
                  </TableCell>
                </TableRow>
              ) : flatLayout ? (
                <FlatConditionRows rows={rows} layout={flatLayout} headerHeight={headerHeight} />
              ) : (
                rows.map((row, idx) => (
                  <TableRow key={idx}>
                    {visibleColumns(rows, columns).map((col) => (
                      <TableCell key={col.key} sx={{ verticalAlign: "top" }}>{formatPbhConditionCell(row, col.key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </Box>
      </Popover>
    </>
  );
};

const RevisionHistoryHeader = ({ showOldValue, item }) => (
  <TableHead>
    <TableRow>
      <TableCell sx={{ minWidth: '30%' }}>Label</TableCell>
      {showOldValue && <TableCell>Old Value</TableCell>}
      <TableCell>{item?.action?.label === 'delete' ? 'Deleted' : 'New'} Value</TableCell>
    </TableRow>
  </TableHead>
);

const RevisionHistoryList = ({ item, showOldValue, moduleName }) => {
  const formatValue = (value, label) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) {
      let val = value.map(i => i?.label || i?.value || '--')?.toString()
      return val ?? '--'
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    if (typeof value === "object") {
      if (value?.label) return value.label;
      if (value?.name) return value.name;
      return "-";
    }
    if (typeof value === "string" && isNaN(value) && moment(value, moment.ISO_8601, true).isValid()) {
      return label === "Updated At"
        ? moment(value).format("DD MMM YYYY, hh:mm A")
        : moment(value).format("DD MMM YYYY");
    }
    return value;
  };

  const isReservePbhCondition = moduleName === "reserve_rates" && item?.field === "pbh_condition";
  const isLeasePbhCondition = moduleName === "lease_rental_rate" && item?.field === "pbh_conditions";
  const isPbhCondition = isReservePbhCondition || isLeasePbhCondition;
  const isIntervalCondition = (moduleName === "reserve_rates" || moduleName === "lease_rental_rate") && item?.field === "interval_condition";
  const isEngineThrustTypes = moduleName === "reserve_rates" && item?.field === "engine_thrust_types";
  const isCustomPayment = moduleName === "lease_rental_rate" && item?.field === "custom_payments";

  const isVariability = moduleName === "insurance" && item?.field === "variabilities";
  const isConditionType = isPbhCondition || isIntervalCondition || isEngineThrustTypes || isCustomPayment || isVariability;
  const staticConditionColumns = isEngineThrustTypes
    ? ENGINE_THRUST_TYPES_COLUMNS
    : isLeasePbhCondition
      ? RENTAL_PBH
      : isCustomPayment
        ? CUSTOM_PAYMENT_COLUMNS
        : isVariability
          ? INSURANCE_VARIABILITY_COLUMNS
          : RESERVE_PBH;;

  const oldConditionColumns = isIntervalCondition ? buildDynamicColumns(item?.old) : staticConditionColumns;
  const newConditionColumns = isIntervalCondition ? buildDynamicColumns(item?.new) : staticConditionColumns;
  // interval condition columns come from the data itself, so compare the whole row there
  const compareColumns = isIntervalCondition ? undefined : staticConditionColumns;
  const fieldLabel = item?.field === "contractual_start_date" ? (gaTelesisInstance ? "Lease Signature Date" : "Contract Date") : toCamelCase(item?.field);

  return (
    <TableRow>
      <TableCell>
        {fieldLabel}
        {isConditionType && showOldValue && (
          <ConditionChangesCell oldValue={item?.old} newValue={item?.new} oldColumns={oldConditionColumns} newColumns={newConditionColumns} compareColumns={compareColumns} />
        )}
      </TableCell>
      {showOldValue && (
        <TableCell>
          {isConditionType ? <PbhConditionCell value={item?.old} otherValue={item?.new ?? null} type="old" columns={oldConditionColumns} compareColumns={compareColumns} /> : formatValue(item?.old, item?.field)}
        </TableCell>
      )}
      <TableCell>
        {isConditionType ? <PbhConditionCell value={item?.new} otherValue={showOldValue ? item?.old ?? null : undefined} type="new" columns={newConditionColumns} compareColumns={compareColumns} /> : formatValue(item?.new, item?.field)}
      </TableCell>
    </TableRow>
  );
};

const buildQueryString = (query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, value);
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

const RevisionHistory = ({ onToggleModal, moduleName, asset_type, query, asset_slug, revisionHistory, section_type }) => {
  const params = useParams()
  const dispatch = useDispatch();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const requestIdRef = useRef(0);

  const type = useMemo(() => (section_type ? { section_type } : {}), [section_type]);
  const handleToggle = (index) => setExpandedIndex(expandedIndex === index ? null : index);

  const buildHistoryUrl = (pageToLoad = 1, perPage = DEFAULT_PAGE_SIZE) => {
    const pageQuery = buildQueryString({ page: pageToLoad, per_page: perPage });

    if (section_type === "delivery" || section_type === "re-delivery") {
      const deliveryQuery = buildQueryString({ delivery_assembly_type: section_type, page: pageToLoad, per_page: perPage });
      if (revisionHistory?.data?.type === "global") {
        return `/audit-logs/lease/${params?.leaseSlug}/history/${moduleName}${deliveryQuery}`;
      }
      if (revisionHistory?.tableName === "event") {
        return `audit-logs/delivery_redelivery_maintenance_event/${revisionHistory?.data?.slug}${deliveryQuery}`;
      }
      return `audit-logs/delivery_redelivery_assembly/${revisionHistory?.data?.slug}${deliveryQuery}`;
    }
    if (revisionHistory?.data?.type === "asset") {
      return `/audit-logs/global/history/${moduleName}${buildQueryString({ asset_type, asset_slug, page: pageToLoad, per_page: perPage })}`;
    }
    if (revisionHistory?.data?.slug) {
      if (revisionHistory?.data?.type === "global") {
        return `/audit-logs/lease/${revisionHistory?.data?.slug}/history/${moduleName}${pageQuery}`;
      }
      return `/audit-logs/${moduleName}/${revisionHistory?.data?.slug}${buildQueryString({
        ...type,
        page: pageToLoad,
        per_page: perPage,
      })}`;
    }
    return `/audit-logs/global/history/${moduleName}${buildQueryString({ page: pageToLoad, per_page: perPage })}`;
  };

  const fetchHistory = async ({ pageToLoad = 1, append = false, perPage = pageSize } = {}) => {
    const requestId = ++requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setHistoryItems([]);
      setPagination(null);
      setExpandedIndex(null);
    }

    try {
      const response = await globalGetService(buildHistoryUrl(pageToLoad, perPage));
      if (requestId !== requestIdRef.current) return;

      const normalized = response?.data?.data;
      const nextList = normalized.list || [];
      const nextPagination = normalized.pagination || null;

      setHistoryItems((prev) => (append ? [...prev, ...nextList] : nextList));
      setPagination(nextPagination);
      setCurrentPage(pageToLoad);
      setPageSize(nextPagination?.per_page || perPage || DEFAULT_PAGE_SIZE);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!revisionHistory?.data && !revisionHistory?.modal) return;

    requestIdRef.current += 1;
    setExpandedIndex(null);
    setHistoryItems([]);
    setPagination(null);
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    fetchHistory({ pageToLoad: 1, append: false, perPage: DEFAULT_PAGE_SIZE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionHistory?.data?.slug, revisionHistory?.data?.type, revisionHistory?.tableName, moduleName, asset_type, asset_slug, section_type]);

  const hasMore = useMemo(() => {
    if (loading || loadingMore) return false;
    if (pagination?.total !== undefined && pagination?.total !== null) {
      return historyItems.length < pagination.total;
    }
    return historyItems.length > 0 && historyItems.length % pageSize === 0;
  }, [historyItems.length, loading, loadingMore, pageSize, pagination?.total]);

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    fetchHistory({ pageToLoad: currentPage + 1, append: true, perPage: pageSize });
  };
  const handleClose = () => {
    dispatch(setRevisonHistory());
    onToggleModal();
  };
  console.log("revisionHistory-->", revisionHistory, moduleName)
  return (
    <Drawer anchor="right" open={true} sx={{ "& .MuiDrawer-paper": { width: 600 } }}>
      <Box sx={{ p: 2, borderBottom: "1px solid #eee", position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 7, }}>
        <Grid container alignItems="center">
          <Grid item size={{ md: 6, xs: 12 }}>
            <Typography variant="h6" color="primary">Revision History</Typography>
          </Grid>
          <Grid item size={{ md: 6, xs: 12 }} sx={{ textAlign: "right" }}>
            <IconButton onClick={handleClose}><CloseIcon /></IconButton>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ position: "relative", pl: 5, pb: 0, pr: 2, mt: "25px", zIndex: 1 }}>
        <Box sx={{ position: "absolute", left: 32, top: 0, bottom: 0, width: "2px", bgcolor: loading ? "rgba(0, 0, 0, 0.11)" : "primary.main", zIndex: 1 }} />
        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "stretch", mb: 3 }}>
                <Box sx={{ position: "relative", minWidth: "15px", height: "30px", top: "-3px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, marginLeft: -2.6 }}>
                  <Skeleton variant="circular" width={30} height={30} />
                </Box>
                <Card sx={{ flex: 1, marginLeft: 2, borderRadius: "12px", border: "1px solid rgba(0, 0, 0, 0.12)", boxShadow: "none", p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item size={{ md: 6, xs: 12 }}>
                      <Skeleton width="80%" height={20} />
                      <Skeleton width="60%" height={20} />
                    </Grid>
                    <Grid item size={{ md: 6, xs: 12 }}>
                      <Skeleton width="80%" height={20} />
                      <Skeleton width="60%" height={20} />
                    </Grid>
                    <Grid item size={{ md: 6, xs: 12 }}>
                      <Skeleton width="40%" height={20} />
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            ))}
          </>
        ) : historyItems?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No revision history available</Typography>
        ) : (
          <InfiniteScroll
            dataLength={historyItems.length}
            next={loadMore}
            hasMore={hasMore}
            loader={<h4 style={{ clear: "both", textAlign: "center", padding: "10px" }}>Loading...</h4>}
            endMessage={pagination?.total === historyItems?.length ? <p style={{ clear: "both", textAlign: "center", padding: "10px" }}>Yay! You have seen it all</p> : null}
            scrollableTarget="revision-history-scroll-container"
            style={{ overflow: "visible", background: "transparent" }}
          >
            {historyItems?.map((item, index) => {
              const logHistory = Object.entries(item?.changes || {}).map(([key, value]) =>
                item?.action?.label === "create"
                  ? { field: key, old: null, new: value }
                  : item?.action?.label === "delete"
                    ? { field: key, old: null, new: value }
                    : { field: key, old: value?.old, new: value?.new }
              );

              const showOldValue = item?.action?.label !== "create" && item?.action?.label !== "delete";

              return (
                <Box key={index} sx={{ display: "flex", alignItems: "stretch", mb: index < historyItems.length - 1 ? 1 : 5 }}>
                  <Box sx={{ position: "relative", minWidth: "15px", height: "30px", top: "-5px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
                    {item?.action?.label === "create"
                      ? <AddIcon sx={{ color: "primary.main", bgcolor: "white", border: "2px solid", borderColor: "primary.main", borderRadius: "50%", fontSize: "20px", boxShadow: 1, zIndex: 2, marginLeft: "-30px", backgroundClip: "padding-box" }} />
                      : item?.action?.label === "delete"
                        ? <Box sx={{ width: "1em", height: "1em", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "white", border: "2px solid", borderColor: "primary.main", borderRadius: "50%", boxShadow: 1, zIndex: 2, marginLeft: "-30px", backgroundClip: "padding-box" }}>
                          <DeleteIcon color='error' sx={{ fontSize: "19px" }} />
                        </Box>
                        : <Box sx={{ width: "1em", height: "1em", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "white", border: "2px solid", borderColor: "primary.main", borderRadius: "50%", boxShadow: 1, zIndex: 2, marginLeft: "-30px", backgroundClip: "padding-box" }}>
                          <EditIcon sx={{ color: "primary.main", fontSize: "15px" }} />
                        </Box>}
                  </Box>
                  <Card sx={{ flex: 1, borderRadius: "12px", border: "1px solid rgba(0, 0, 0, 0.12)", boxShadow: "none", p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item size={{ md: item?.category ? 4 : 6, xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">
                          {item?.action?.label === "create" ? "Created at" : item?.action?.label === "delete" ? "Deleted at" : "Updated at"}
                        </Typography>
                        <Typography variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{moment(item?.timestamp).format("DD MMM YYYY, hh:mm A")}</Typography>
                        <Typography variant="body1">{item.createdAt}</Typography>
                      </Grid>
                      {
                        item?.category &&
                        <Grid item size={{ md: 4, xs: 12 }}>
                          <Typography variant="body2" color="text.secondary">
                            {"Category"}
                          </Typography>
                          <Typography variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{item?.category}</Typography>
                          <Typography variant="body1">{item.createdAt}</Typography>
                        </Grid>
                      }
                      <Grid item size={{ md: item?.category ? 4 : 6, xs: 12 }} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end" }}>
                        <Typography variant="body2" color="text.secondary">
                          {item?.action?.label === "create" ? "Created by" : item?.action?.label === "delete" ? "Deleted by" : "Updated by"}
                        </Typography>
                        <Typography variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{item?.user?.name}</Typography>
                        <Typography variant="body1">{item.createdBy}</Typography>
                      </Grid>
                      <Grid item size={{ md: 6, xs: 12 }}>
                        <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => handleToggle(index)}>
                          <Typography variant="body2" color="primary">{expandedIndex === index ? "Hide Data" : "Show Data"}</Typography>
                          <ExpandMoreIcon sx={{ fontSize: 18, ml: 0.5, color: "primary.main", transform: expandedIndex === index ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }} />
                        </Box>
                      </Grid>
                      {expandedIndex === index && (
                        <Grid item size={{ md: 12, xs: 12 }}>
                          {(moduleName === "utilization" || moduleName === "utilization_master") && item?.utilization_asset ? <Typography display="flex" variant="body2" color="text.secondary">Asset : <Typography sx={{ ml: "2px" }} variant="body2" color="rgb(0, 0, 0)" className="revision-history-content">{item?.utilization_asset}</Typography></Typography> : null}
                          <Box sx={{ mt: 1, p: 2, backgroundColor: "rgb(246, 246, 246)", borderRadius: "8px" }}>
                            <Table className="mui-table-format" stickyHeader>
                              <RevisionHistoryHeader showOldValue={showOldValue} item={item} />
                              <tbody>
                                {logHistory.map((historyItem, idx) => (
                                  <RevisionHistoryList key={idx} item={historyItem} showOldValue={showOldValue} moduleName={moduleName} />
                                ))}
                              </tbody>
                            </Table>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                </Box>
              );
            })
            }</InfiniteScroll>)}
        {loadingMore && !loading ? <Box sx={{ textAlign: "center", py: 2 }}>Loading...</Box> : null}
      </Box>
    </Drawer>
  );
};

export default RevisionHistory;
